import { observer } from "mobx-react-lite";
import React from "react";
import { View, XStack, YStack } from "tamagui";
import { Body } from "../../ui/Body";
import { Sheet, type SheetProps } from "../../ui/Sheet";
import { Subtext } from "../../ui/Subtext";
import { Title } from "../../ui/Title";
import { AvatarSize, UserPropsAvatar } from "../../ui/UserAvatar";
import { useDisplayNameInfo } from "../feed/utils";
import type {
  BusinessPlanModel,
  BusinessStaticUserInfoWithUserUid,
} from "../plan/BusinessPlanModel";
import { BusinessTeamInfo } from "../plan/ui/BusinessTeamInfo";
import { BusinessProfileQuestions } from "./BusinessProfileQuestions";

export const BusinessUserInfoSheet: React.FC<
  SheetProps & {
    userInfo: BusinessStaticUserInfoWithUserUid | null;
    businessPlan: BusinessPlanModel;
  }
> = observer(({ businessPlan, userInfo, open, ...props }) => {
  const getDisplayTag = useDisplayNameInfo();

  if (!open || !userInfo) {
    return null;
  }

  const displayInfo = getDisplayTag(userInfo);

  const showDescription = !!userInfo.profile?.description;
  const showProfileQuestions =
    !!userInfo.profile?.profileQuestions &&
    Object.keys(userInfo.profile?.profileQuestions).length > 0;

  const Icon = displayInfo?.icon;
  const emoji =
    typeof displayInfo?.icon === "string" ? displayInfo.icon : undefined;

  return (
    <Sheet open={open} {...props} sheetId="b-user">
      <YStack gap="$space.large" alignItems={"center"}>
        <UserPropsAvatar {...userInfo} size={AvatarSize.Large} />
        {displayInfo && (
          <XStack gap={"$small"} alignItems={"center"}>
            {Icon && <Icon />}
            {emoji && (
              <View>
                <Subtext>{emoji}</Subtext>
              </View>
            )}
            <Body>{displayInfo.text}</Body>
          </XStack>
        )}
        <Title>{userInfo.displayName}</Title>

        {showDescription && <Body>{showDescription}</Body>}

        {showProfileQuestions && (
          <BusinessProfileQuestions
            userInfo={userInfo}
            businessPlan={businessPlan}
            center
          />
        )}

        <BusinessTeamInfo businessPlan={businessPlan} userInfo={userInfo} />
      </YStack>
    </Sheet>
  );
});
